import React from 'react';
import { formatRelativeTime } from '../utils/dateUtils';

// A small status pill that renders "{label} {relativeTime}" with a colored dot.
// Color shifts as the timestamp ages: green (<1h, with ping) → blue (<24h) → amber (older).
// Returns null when `date` is falsy, so callers can drop their own conditional wrappers.
//
// Pass `overdueAfterHours` when the timestamp is produced by something on a known
// schedule. The default tiers assume minutes-to-hours freshness, so against a job that
// runs every two weeks the amber tier is lit permanently and stops meaning anything.
// Given an expected interval the tiers become relative to it, and passing it turns red
// once the timestamp is older than the job's own period — which is the only reliable
// signal that a scheduled sync has stopped working.
const TONES = {
    fresh: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', dot: 'bg-emerald-500', ping: 'bg-emerald-400' },
    recent: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', dot: 'bg-blue-500', ping: null },
    stale: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', dot: 'bg-amber-500', ping: null },
    overdue: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500', ping: 'bg-red-400' },
};

const pickTone = (date, overdueAfterHours) => {
    const ageHours = (Date.now() - new Date(date).getTime()) / 3_600_000;

    if (overdueAfterHours) {
        if (ageHours >= overdueAfterHours) return TONES.overdue;
        if (ageHours >= overdueAfterHours * 0.75) return TONES.stale;
        return TONES.recent;
    }

    if (ageHours < 1) return TONES.fresh;
    if (ageHours < 24) return TONES.recent;
    return TONES.stale;
};

const RelativeTimePill = ({ date, label, className = '', overdueAfterHours = null, overdueLabel = 'overdue' }) => {
    if (!date) return null;
    const tone = pickTone(date, overdueAfterHours);
    const isOverdue = tone === TONES.overdue;
    return (
        <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-medium border ${tone.bg} ${tone.border} ${tone.text} ${className}`}>
            <span className="relative flex h-2 w-2 shrink-0">
                {tone.ping && (
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${tone.ping} opacity-75`} />
                )}
                <span className={`relative inline-flex rounded-full h-2 w-2 ${tone.dot}`} />
            </span>
            {label} {formatRelativeTime(date)}
            {isOverdue && <span className="font-semibold">· {overdueLabel}</span>}
        </span>
    );
};

export default RelativeTimePill;
